// NPM MODULES
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Grid,
  Typography,
  CircularProgress,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
} from '@mui/material';
import { ChevronLeft } from '@mui/icons-material';
import { DateTime } from 'luxon';

// FORMATTED MESSAGES
import ScheduledFormattedMessages from '../../../messages/websms/ScheduledFormattedMessages';

// NETWORK
import useScheduledRequest from '../../../requests/ScheduledRequest';

// COMPONENTS
import SectionWrapper from '../../common/SectionWrapper';
import ConfirmationDialog from '../sent/ConfirmationDialog';

// UTILS
import { getZoneOffsettedDate } from '../../../utils/dates';
import {
  getMessageMaxLength,
  getCharacterCount,
  withinCharacterLimit,
  getMessageParts,
} from '../../../utils/characterCount';

const SentScheduledGroup = ({
  smsId,
  handleBackButton,
  failed = false,
  errorHandler,
}) => {
  const [justCancelled, setJustCancelled] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [scheduledSms, setScheduledSms] = useState(null);
  const [cancelList, setCancelList] = useState(new Set());
  const [messageContent, setMessageContent] = useState('');

  const { cancelPendingMessage, getScheduledItem, updateScheduledMessage } =
    useScheduledRequest();

  const fetchSms = async () => {
    setScheduledSms(null);
    setMessageContent('');

    const scheduledSms = await getScheduledItem(smsId, errorHandler);
    console.log('scheduledSms:', scheduledSms);
    setScheduledSms(scheduledSms);

    let smsText;
    try {
      smsText = decodeURIComponent(scheduledSms.sms_text);
    } catch (err) {
      smsText = 'ERROR: MALFORMED TEXT';
    }

    setMessageContent(smsText);
  };

  useEffect(() => {
    fetchSms();
  }, [smsId]);

  const handleCancelPendingMessage = async () => {
    setShowConfirmation(false);
    const result = await cancelPendingMessage(smsId, errorHandler);
    if (result && result.length > 0 && setJustCancelled) {
      setJustCancelled(true);
    }
    fetchSms();
  };

  const handleUpdateScheduledMessage = async () => {
    setShowEditModal(false);
    const result = await updateScheduledMessage(
      smsId,
      { sms_text: messageContent },
      errorHandler
    );
    console.log('result:', result);
    fetchSms();
  };

  const generateEditDialog = () => {
    const { characterCount, isUnicodeMessage } =
      getCharacterCount(messageContent);

    const messagePartCount = getMessageParts(messageContent);

    const messageWithinLength = withinCharacterLimit(
      messageContent,
      isUnicodeMessage
    );
    const messageMaxLength = getMessageMaxLength(isUnicodeMessage);

    return (
      <Dialog
        open={showEditModal}
        onClose={() => setShowEditModal(false)}
        aria-labelledby="cancel-dialog-title"
        aria-describedby="cancel-dialog-description"
      >
        <DialogTitle id="cancel-dialog-title" variant="h2">
          EDIT GROUP MESSAGE DETAILS
        </DialogTitle>

        <DialogContent>
          <DialogContentText id="cancel-dialog-description" variant="p">
            {'Update message content for just the message'}
            <br />
            <br />
          </DialogContentText>
          <TextField
            multiline
            rows={3}
            value={messageContent || ''}
            id="messageContent"
            name="messageContent"
            onChange={(e) => setMessageContent(e.target.value)}
            // inputRef={messageContentRef}
            slotProps={{
              htmlInput: {
                maxLength: getMessageMaxLength(false),
              },
            }}
            fullWidth
          />
          <DialogContentText id="cancel-dialog-description" variant="p">
            {characterCount} / {messageMaxLength} = {characterCount}{' '}
            {isUnicodeMessage ? 'Unicode' : 'Standard'} {'Character(s)'}
            <br />
            {messagePartCount} {'Message Parts'}
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setShowEditModal(false)}
            color="secondary"
            variant="outlined"
          >
            Cancel
          </Button>

          <Button
            onClick={handleUpdateScheduledMessage}
            color="secondary"
            variant="contained"
            autoFocus
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const formatDate = (date) => {
    const timeShort = localStorage.getItem('timeShort');
    const timeString = localStorage.getItem('timeString');

    const dateObj = DateTime.fromISO(date);
    const dateRezoned = dateObj.setZone(timeShort, {
      keepLocalTime: false,
    });

    return `${dateRezoned.toFormat('dd-LLL-yyyy h:mm a')} ${timeString}`;
  };

  const generateBody = () => {
    if (scheduledSms) {
      let smsText;
      try {
        smsText = decodeURIComponent(scheduledSms.sms_text);
      } catch (err) {
        smsText = 'ERROR: MALFORMED TEXT';
      }

      return (
        <Box>
          <Typography gutterBottom>
            <strong>{ScheduledFormattedMessages.queuedHeader}</strong>
            {`: ${formatDate(scheduledSms.sms_sent)}`}
          </Typography>
          <Typography gutterBottom>
            <strong>{'To'}</strong>
            {`: ${scheduledSms.dest_tn}`}
          </Typography>
          <Typography gutterBottom>
            <strong>{'Status'}</strong>
            {`: ${scheduledSms.status}`}
          </Typography>
          <Typography gutterBottom>
            <strong>{'Message'}</strong>
            {`: ${smsText}`}
          </Typography>
          <Typography gutterBottom>
            <strong>{'Scheduled on'}</strong>
            {`: ${formatDate(scheduledSms.created_date)}`}
          </Typography>
          <Typography gutterBottom>
            <strong>{'Modified on'}</strong>
            {`: ${formatDate(scheduledSms.updated_date)}`}
          </Typography>
          {!justCancelled && (
            <Stack sx={{ flexDirection: 'row', gap: 2 }}>
              {
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => setShowConfirmation(true)}
                  sx={{ marginY: 2 }}
                >
                  {'Cancel Message'}
                </Button>
              }

              <Button
                variant="contained"
                color="secondary"
                onClick={() => setShowEditModal(true)}
                sx={{ marginY: 2 }}
              >
                {'Edit Message'}
              </Button>
            </Stack>
          )}
        </Box>
      );
    }

    return <CircularProgress />;
  };

  return (
    <Grid container>
      <Grid size={{ xs: 12, lg: 9 }}>
        <SectionWrapper title="SMS Information" icon="schedule">
          <Box>
            <Button
              startIcon={<ChevronLeft />}
              onClick={handleBackButton}
              color="secondary"
              sx={{ marginBottom: 2 }}
            >
              <Typography>{'Back to Scheduled Group'}</Typography>
            </Button>
          </Box>

          <Typography variant="h3" gutterBottom>
            {ScheduledFormattedMessages.replyDetails}
          </Typography>

          {generateBody()}

          <ConfirmationDialog
            showConfirmation={showConfirmation}
            setShowConfirmation={() => setShowConfirmation(false)}
            setJustCancelled={setJustCancelled}
            message={
              'Are you sure you would like to cancel this pending message?'
            }
            handleCancelPendingMessage={handleCancelPendingMessage}
          />
          {generateEditDialog()}
        </SectionWrapper>
      </Grid>
    </Grid>
  );
};

export default SentScheduledGroup;
